/**
 * Comprehensive whitespace handling utilities
 * Handles ASCII and Unicode whitespace characters
 */

/**
 * Regex pattern to match all types of whitespace characters:
 * - \s: Standard whitespace (space, tab, newline, carriage return, form feed, vertical tab)
 * - \u00A0: Non-breaking space (NBSP)
 * - \u1680: Ogham space mark
 * - \u2000-\u200A: Various Unicode spaces (en quad, em quad, en space, em space, three-per-em space, four-per-em space, six-per-em space, figure space, punctuation space, thin space, hair space)
 * - \u2028: Line separator
 * - \u2029: Paragraph separator
 * - \u202F: Narrow no-break space
 * - \u205F: Medium mathematical space
 * - \u3000: Ideographic space
 */
const WHITESPACE_REGEX = /[\s\u00A0\u1680\u2000-\u200A\u2028\u2029\u202F\u205F\u3000]/g

/**
 * Regex to detect if a string contains ONLY whitespace characters
 */
const IS_BLANK_REGEX = /^[\s\u00A0\u1680\u2000-\u200A\u2028\u2029\u202F\u205F\u3000]+$/

/**
 * Regex to trim whitespace from both ends (leading and trailing)
 */
const TRIM_REGEX = /^[\s\u00A0\u1680\u2000-\u200A\u2028\u2029\u202F\u205F\u3000]+|[\s\u00A0\u1680\u2000-\u200A\u2028\u2029\u202F\u205F\u3000]+$/g

/**
 * Regex to trim leading whitespace only
 */
const TRIM_START_REGEX = /^[\s\u00A0\u1680\u2000-\u200A\u2028\u2029\u202F\u205F\u3000]+/

/**
 * Regex to trim trailing whitespace only
 */
const TRIM_END_REGEX = /[\s\u00A0\u1680\u2000-\u200A\u2028\u2029\u202F\u205F\u3000]+$/

/**
 * Regex to match consecutive whitespace characters (2 or more)
 */
const CONSECUTIVE_WHITESPACE_REGEX = /[\s\u00A0\u1680\u2000-\u200A\u2028\u2029\u202F\u205F\u3000]{2,}/g

/**
 * Removes all whitespace characters from a string
 * @param str - The input string
 * @returns String with all whitespace removed
 */
export const removeAllWhitespace = (str: string): string => {
  return str.replace(WHITESPACE_REGEX, '')
}

/**
 * Checks if a string contains only whitespace characters (or is empty)
 * @param str - The input string
 * @returns true if string is blank or contains only whitespace, false otherwise
 */
export const isBlankOrWhitespace = (str: string): boolean => {
  return str.length === 0 || IS_BLANK_REGEX.test(str)
}

/**
 * Trims whitespace from both ends of a string
 * @param str - The input string
 * @returns Trimmed string
 */
export const trimWhitespace = (str: string): string => {
  return str.replace(TRIM_REGEX, '')
}

/**
 * Trims whitespace from the start (left side) of a string
 * @param str - The input string
 * @returns String with leading whitespace removed
 */
export const trimStart = (str: string): string => {
  return str.replace(TRIM_START_REGEX, '')
}

/**
 * Trims whitespace from the end (right side) of a string
 * @param str - The input string
 * @returns String with trailing whitespace removed
 */
export const trimEnd = (str: string): string => {
  return str.replace(TRIM_END_REGEX, '')
}

/**
 * Replaces consecutive whitespace characters with a single space
 * Useful for normalizing input where multiple spaces should become one
 * @param str - The input string
 * @returns String with consecutive whitespace collapsed to single space
 */
export const collapseWhitespace = (str: string): string => {
  return str.replace(CONSECUTIVE_WHITESPACE_REGEX, ' ')
}

/**
 * Replaces ALL Unicode whitespace characters (including single ones) with ASCII space
 * Useful for normalizing Unicode spaces that shouldn't appear in form fields
 * @param str - The input string
 * @returns String with all Unicode whitespaces replaced with ASCII spaces
 */
export const replaceUnicodeWhitespaceWithSpace = (str: string): string => {
  return str.replace(WHITESPACE_REGEX, ' ')
}

/**
 * Normalizes whitespace: trims edges and collapses internal consecutive spaces
 * This is useful for form fields where you want clean, normalized input
 * @param str - The input string
 * @returns Normalized string
 */
export const normalizeWhitespace = (str: string): string => {
  return trimWhitespace(collapseWhitespace(str))
}

/**
 * Sanitizes input by removing problematic characters and normalizing whitespace
 * Used for form fields like names, emails, etc.
 * @param str - The input string
 * @param options - Configuration options
 * @returns Sanitized string
 */
export const sanitizeWhitespace = (
  str: string,
  options: {
    trim?: boolean;           // Trim leading/trailing whitespace (default: true)
    collapse?: boolean;       // Collapse consecutive whitespace (default: true)
    removeAll?: boolean;      // Remove all whitespace completely (default: false)
  } = {}
): string => {
  const { trim = true, collapse = true, removeAll = false } = options

  if (removeAll) {
    return removeAllWhitespace(str)
  }

  let result = str
  if (collapse) {
    result = collapseWhitespace(result)
  }
  if (trim) {
    result = trimWhitespace(result)
  }

  return result
}

/**
 * Converts literal Unicode escape sequences in a string to actual Unicode characters
 * For example: converts the literal string "\u00A0" (7 characters) to actual NBSP character
 * This handles cases where users paste or input literal escape sequences
 * @param str - The input string that may contain literal Unicode escape sequences
 * @returns String with literal escape sequences converted to actual Unicode characters
 */
export const convertLiteralUnicodeToActual = (str: string): string => {
  // Match patterns like \uXXXX or \xXX and convert them to actual Unicode characters
  return str
    .replace(/\\u([0-9a-fA-F]{4})/g, (match, hex) => {
      return String.fromCharCode(parseInt(hex, 16))
    })
    .replace(/\\x([0-9a-fA-F]{2})/g, (match, hex) => {
      return String.fromCharCode(parseInt(hex, 16))
    })
}

/**
 * Character reference guide for Unicode spaces:
 * 
 * ASCII/Standard:
 * - \s             : [ \t\n\r\f\v] (space, tab, newline, carriage return, form feed, vertical tab)
 * 
 * Unicode Spaces:
 * - \u00A0         : Non-breaking space (NBSP)
 * - \u1680         : Ogham space mark
 * - \u2000         : En quad
 * - \u2001         : Em quad
 * - \u2002         : En space
 * - \u2003         : Em space
 * - \u2004         : Three-per-em space
 * - \u2005         : Four-per-em space
 * - \u2006         : Six-per-em space
 * - \u2007         : Figure space
 * - \u2008         : Punctuation space
 * - \u2009         : Thin space
 * - \u200A         : Hair space
 * - \u2028         : Line separator
 * - \u2029         : Paragraph separator
 * - \u202F         : Narrow no-break space (NNBSP)
 * - \u205F         : Medium mathematical space (MMSP)
 * - \u3000         : Ideographic space (used in CJK text)
 */
